"use client";
import ArrowDownRightIcon from "@heroicons/react/24/solid/ArrowDownRightIcon";
import MapPinIcon from "@heroicons/react/24/solid/MapPinIcon";
import AvatarDecoration from "@/components/views/avatar-decoration/AvatarDecoration";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";
import { useVenueDetail } from "@/hooks/venueContext";
import { DEFAULT_IMAGES } from "@/lib/constants";
import { cn } from "@/lib/utils";
import S3Image from "@/components/S3Image";
import { WHATSAPP_TAGS } from "@/@core/enums/whatsapp_tags.enum";
import useWhatsappContext from "@/hooks/useWhatsappContext";
import WhatsappLinkWithBeacon from "@/components/WhatsappLinkWithBeacon";
import useGetWpHref from "@/hooks/useGetWpHref";
import { Container } from "@/components/ui/container";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";
import dynamic from "next/dynamic";
import { useTranslation } from "@/hooks/translationContext";

const WorkerDetailModal = dynamic(
  () => import("@/components/views/modals/worker/WorkerDetailModal"),
);

interface Props extends Worker.List { }

export default function TrainerCard(trainer: Props) {
  const { t } = useTranslation();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { setVenue } = useVenueDetail();
  const whatsappTags = useWhatsappContext();
  const getWhatsappHrefUrl = useGetWpHref();
  const [imgSrc, setImgSrc] = useState(
    `user/${trainer.id}/${trainer.profile_picture}`,
  );

  const whatsappContext = whatsappTags.getContext({
    trainer: `${trainer?.firstname} ${trainer?.lastname}`,
  });

  const handleClick = () => {
    setVenue(trainer.job?.venue as Venue.List);
    setModalIsOpen(true);
  };

  const getTrainerFullName = () => {
    return `${trainer.firstname} ${trainer.lastname}`;
  };

  return (
    <>
      <div
        role="button"
        onClick={() => handleClick()}
        className="flex justify-between items-center p-4 w-full lg:w-[389.33px] gap-2 border-gray border rounded-[24px]"
      >
        <div className="flex gap-2">
          {!trainer?.profile_picture && (
            <AvatarDecoration className="rounded-2xl h-[92px]">
              <S3Image
                sizes="92px"
                width={92}
                height={92}
                className="object-cover object-center rounded-2xl border border-white w-[92px] h-[92px]"
                src={DEFAULT_IMAGES.WORKER}
                loading="lazy"
                alt="trainer"
                style={{
                  maxWidth: "100%",
                }}
              />
            </AvatarDecoration>
          )}

          {trainer?.profile_picture && (
            <AvatarDecoration className="rounded-2xl h-[92px]">
              <S3Image
                sizes="92px"
                width={92}
                height={92}
                className="object-cover object-center rounded-2xl border border-white w-[92px] h-[92px]"
                src={imgSrc}
                onError={(e) => setImgSrc(DEFAULT_IMAGES.WORKER)}
                loading="lazy"
                alt="trainer"
                style={{
                  maxWidth: "100%",
                }}
              />
            </AvatarDecoration>
          )}

          <div className="flex flex-col justify-between h-[92px]">
            <div className="div flex flex-col gap-2">
              <h2 className="font-bold text-base">{getTrainerFullName()}</h2>

              {trainer?.job?.occupation && (
                <span className="font-medium text-xs text-assistant">
                  {trainer?.job?.occupation?.descriptions?.[0]?.title}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              {Array.isArray(trainer.job?.venue?.descriptions) &&
                trainer.job?.venue.descriptions[0]?.title && (
                  <div className="flex items-center gap-1">
                    <ArrowDownRightIcon className="size-4 text-assistant" />
                    <span className="text-ellipsis line-clamp-1 text-gray-400 text-xs font-medium">
                      {trainer.job?.venue.descriptions[0].title}
                    </span>
                  </div>
                )}

              {trainer.job?.venue?.district?.title && (
                <div className="flex items-center gap-1">
                  <MapPinIcon className="size-4 text-assistant" />
                  <span className="text-gray-400 text-[10px] font-medium">
                    {trainer.job?.venue?.district.title}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {trainer.phone && (
          <WhatsappLinkWithBeacon
            title={t("worker-contact-button", { trainer: getTrainerFullName() })}
            disabled={!trainer?.phone}
            href={getWhatsappHrefUrl({
              phone: trainer?.phone,
              tag: WHATSAPP_TAGS.WORKER,
              translateValues: whatsappContext,
            })}
          >
            <Container
              disabled={!trainer?.phone}
              variant="assistant"
              className={cn(
                "flex cursor-pointer active:!mx-0 !h-6 !py-1 !px-2 items-center justify-center",
                !trainer.phone ? "cursor-not-allowed" : "",
              )}
            >
              <WhatsappIcon className="size-4" />
            </Container>
          </WhatsappLinkWithBeacon>
        )}
      </div>

      {modalIsOpen && (
        <WorkerDetailModal
          worker={trainer as Worker.List}
          isOpen={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
        />
      )}
    </>
  );
}

function SkeletonTrainerCard() {
  return (
    <>
      {Array.from(Array(3).keys()).map((index: number) => (
        <div
          key={index}
          className="flex cursor-pointer items-center p-4 w-full lg:w-[389.33px] gap-2 border-gray border rounded-[24px]"
        >
          <div className="flex gap-2">
            <Skeleton className="w-[92px] h-[92px] object-cover object-center rounded-2xl border border-white" />
          </div>
          <div className="flex flex-col justify-between h-[92px]">
            <div className="div flex flex-col gap-2">
              <Skeleton className="w-40 h-5" />
              <Skeleton className="w-16 h-4" />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-2/3 h-4" />
              </div>
              <div className="flex items-center gap-1">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-1/3 h-4" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

export function TrainerLoading({ count = 6 }: { count: number }) {
  return (
    <div
      id="trainer-card-skeleton"
      className="grid justify-items-center grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
    >
      {new Array(count).fill(0).map((_, index) => (
        <SkeletonTrainerCard key={index} />
      ))}
    </div>
  );
}
